import React from 'react';
import Header from '../../components/public/Header';
import Footer from '../../components/public/Footer';
import '../../styles/portal.css';

const LOGO_URL = '/woss-publishing-assets/images/woss-publishing-logo-white.svg';
const LAST_UPDATED = 'July 30, 2026';
const COMPANY = 'Woss Music Publishing Group LLC d/b/a Woss Publishing';
const ADDRESS = '19266 Coastal Hwy, Unit 4-564, Rehoboth Beach, Delaware 19971, US';
const EMAIL = 'global@wosspublishing.com';

export default function PrivacyPolicy() {
  return (
    <div className="page-wrapper">
      <Header />
      <main className="main-wrapper">
        <section style={{ minHeight: '80vh', padding: '5rem 0 6rem' }}>
          <div className="padding-global">
            <div className="container-large" style={{ maxWidth: '820px' }}>

              {/* Logo + title block */}
              <div style={{ textAlign: 'center', marginBottom: '3.5rem', borderBottom: '1px solid rgba(255,255,255,0.06)', paddingBottom: '3rem' }}>
                <img src={LOGO_URL} alt="Woss Publishing" style={{ height: '2.5rem', width: 'auto', marginBottom: '2rem' }} />
                <h1 className="heading-style-h2" style={{ marginBottom: '0.5rem' }}>Privacy Policy</h1>
                <p className="text-size-md text-color-secondary">Last Updated: {LAST_UPDATED}</p>
              </div>

              <div className="wp-legal-body">

                <h2>Who We Are</h2>

                <p>This Site is operated by {COMPANY} ("{COMPANY.split(' d/b/a ')[1] || 'Woss Publishing'}," "we," "our," or "us"), a Delaware limited liability company. We are responsible for the collection and processing of personal data submitted through this Site and the Woss Publishing portal.</p>

                <p>
                  <strong>Mailing address:</strong><br />
                  {ADDRESS}
                </p>

                <h2>Contact</h2>

                <p>Questions, comments, or requests regarding this Privacy Policy or your personal data should be directed to: <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.</p>

                <h2>Information We Collect</h2>

                <p><strong>Information you provide to us directly:</strong></p>
                <ul>
                  <li>Registration details: name, email address, phone number, and other information submitted when accepting an invitation to use the Site.</li>
                  <li>Account information: login credentials, account preferences, and profile data.</li>
                  <li>Contact form submissions: name, email address, phone number, company or role, and the content of your message.</li>
                  <li>Correspondence: content of messages you send us by email or through the Site.</li>
                </ul>

                <p><strong>Information we collect automatically:</strong></p>
                <ul>
                  <li>Technical information: IP address, browser type and version, operating system, time zone, and device identifiers.</li>
                  <li>Usage data: pages visited, links clicked, time spent on pages, referring URLs, and error logs.</li>
                  <li>Session information: authentication tokens stored in your browser session for the duration of your logged-in session.</li>
                </ul>

                <p><strong>Information from your agreement with us:</strong> If you are a Woss Publishing artist or songwriter, information needed to calculate and present royalty data within your account — including composition registrations, split sheets, ownership documents, and royalty statements — may have been obtained from you and stored as part of your publishing agreement with Woss Publishing.</p>

                <h2>How We Use Your Information</h2>

                <p>We may use information we hold about you in the following ways:</p>
                <ul>
                  <li>To create and manage your account on the Site and verify your identity.</li>
                  <li>To provide you with access to royalty statements, song registrations, ownership documents, catalog analytics, and other portal features.</li>
                  <li>To respond to your inquiries, support requests, or contact form submissions.</li>
                  <li>To carry out our obligations arising from any contracts entered into between you and us.</li>
                  <li>To notify you about changes to the Site or your account.</li>
                  <li>To maintain the security and integrity of the Site.</li>
                  <li>To comply with legal and regulatory obligations.</li>
                  <li>For internal operations, including troubleshooting, data analysis, and service improvement.</li>
                  <li>To manage sync licensing inquiries and, where appropriate, connect your catalog with relevant creative opportunities.</li>
                </ul>

                <p>If you are a Woss Publishing artist or songwriter, we use information collected through the Site solely to facilitate royalty reporting, catalog management, and the administration of our publishing relationship with you, as otherwise described in your agreement with Woss Publishing.</p>

                <h2>Disclosure of Your Information</h2>

                <p>We do not sell your personal data. We may share your personal data with:</p>
                <ul>
                  <li><strong>Publishing partners:</strong> Information may be shared with Warner Chappell Music and other publishing administration partners as necessary to perform royalty processing and rights management services.</li>
                  <li><strong>Service providers:</strong> Third-party vendors we use to operate the Site (e.g., cloud hosting, email delivery, security), who are contractually bound to use your data only to provide services to us.</li>
                  <li><strong>Legal obligations:</strong> If required by law, court order, or regulatory authority, we may disclose your data to the extent necessary to comply.</li>
                  <li><strong>Business transfers:</strong> In the event of a merger, acquisition, or sale of all or substantially all of our assets, your data may be transferred as part of that transaction.</li>
                </ul>

                <p>We do not disclose your personal data to third-party advertisers or marketing networks.</p>

                <h2>Data Retention</h2>

                <p>We retain your personal data for as long as your account is active or as needed to provide you with services, and as required to comply with our legal obligations, resolve disputes, and enforce our agreements. When data is no longer needed, we will delete or anonymize it in accordance with applicable law.</p>

                <h2>Security</h2>

                <p>We have implemented technical and organizational measures designed to protect the personal data we collect. However, no method of transmission over the internet or electronic storage is completely secure. While we strive to use commercially acceptable means to protect your data, we cannot guarantee absolute security. You access the Site and transmit data at your own risk. You are responsible for keeping your login credentials confidential.</p>

                <h2>Cookies and Tracking</h2>

                <p>The Site may use cookies and similar technologies to maintain your authenticated session, remember your preferences, and analyze Site usage. Session cookies are deleted when you close your browser. You may configure your browser to refuse cookies; however, doing so may prevent certain features of the Site from functioning correctly.</p>

                <p>We use basic analytics to understand how visitors use the Site (e.g., pages visited, time on site). This data is collected in aggregate and is not used to identify individual users for marketing purposes.</p>

                <h2>Third-Party Links</h2>

                <p>The Site may contain links to third-party websites (for example, social media profiles of roster artists). These websites have their own privacy policies, and we are not responsible for their content or practices. We encourage you to review the privacy policies of any third-party sites you visit.</p>

                <h2>Children</h2>

                <p>This Site is not directed toward children under the age of 13, and we do not knowingly collect personal data from children. If you believe a child has provided us with personal data, please contact us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a> and we will take steps to delete it.</p>

                <h2>Your Rights</h2>

                <p>Depending on your jurisdiction, you may have the right to:</p>
                <ul>
                  <li>Access the personal data we hold about you.</li>
                  <li>Request correction of inaccurate or incomplete data.</li>
                  <li>Request deletion of your personal data (subject to legal retention obligations).</li>
                  <li>Object to or request restriction of certain processing activities.</li>
                  <li>Request portability of data you have provided to us.</li>
                </ul>

                <p>To exercise any of these rights, please contact us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a>. We may require you to verify your identity before processing your request. We will respond within the timeframe required by applicable law.</p>

                <h2>California Residents</h2>

                <p>California residents may have additional rights regarding their personal data under the California Consumer Privacy Act (CCPA). We do not sell personal information as defined under CCPA. To exercise your rights under California law, please contact us at <a href={`mailto:${EMAIL}`}>{EMAIL}</a>.</p>

                <h2>Changes to This Policy</h2>

                <p>We may update this Privacy Policy from time to time. When we make material changes, we will post the updated policy on this page and update the "Last Updated" date. We encourage you to review this policy periodically. Your continued use of the Site after any changes constitutes your acceptance of the revised policy.</p>

                <h2>Contact Us</h2>

                <p>
                  <strong>{COMPANY}</strong><br />
                  Attn: Privacy<br />
                  {ADDRESS}<br />
                  Email: <a href={`mailto:${EMAIL}`}>{EMAIL}</a>
                </p>

              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}
